import { PanelsTopLeft, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { LoadingRows, SearchInput } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { getPanel, listPanels, toPanelBody, updatePanel, type Panel } from '@/lib/panels'
import { useSelection } from '@/lib/useSelection'
import { plural } from '@/lib/utils'

import { PanelPreview } from './PanelPreview'
import { ScopeChips, TypeBadge, UrlRow, selectClass } from './_shared'
import type { BlockType } from './config-types'

/** The panel's composition: one neutral tag per block type (deduped). */
function BlockTags({ types }: { types: string[] }) {
  const unique = Array.from(new Set(types))
  if (unique.length === 0) return <span className="text-text-3">—</span>
  return (
    <span className="flex gap-1 overflow-hidden">
      {unique.map((t) => <TypeBadge key={t} type={t as BlockType} />)}
    </span>
  )
}

/** Enabled toggle. The admin PUT replaces the whole record and this row is a snapshot
 * from when the list loaded, so the body is built from a fresh read, never from the row. */
function EnabledSwitch({ panel, onUpdated }: { panel: Panel; onUpdated: (p: Panel) => void }) {
  // Optimistic value while the request runs; null = show what the server last returned.
  const [pending, setPending] = useState<boolean | null>(null)
  // `pending` disables the switch only after a re-render; the ref blocks a second
  // toggle that lands before it.
  const busyRef = useRef(false)

  async function toggle(next: boolean) {
    if (busyRef.current) return
    busyRef.current = true
    setPending(next)
    try {
      // Someone may have edited this panel (name, blocks, scope) since the list loaded;
      // echoing the list's copy would silently revert that edit.
      const fresh = await getPanel(panel.id)
      const updated = await updatePanel(panel.id, { ...toPanelBody(fresh), enabled: next })
      onUpdated(updated)
    } catch (e) {
      toast.error(`Failed to update "${panel.name}": ${(e as Error).message}`)
    } finally {
      busyRef.current = false
      setPending(null)
    }
  }

  return (
    // the row selects on click — the switch is its own control
    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="inline-flex">
      <Switch
        checked={pending ?? panel.enabled}
        disabled={pending !== null}
        onCheckedChange={(next) => void toggle(next)}
        aria-label={`Enable ${panel.name}`}
      />
    </span>
  )
}

type FacetValue = 'any' | string

function Facet({ label, value, options, onChange }: {
  label: string; value: FacetValue; options: { value: string; label: string }[]; onChange: (v: FacetValue) => void
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-3">{label}</span>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
        <option value="any">any</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

function PanelDetail({ panel }: { panel: Panel }) {
  return (
    <DetailPane
      eyebrow="Panel"
      title={panel.name}
      status={(
        // Edit sits on the status line, below the drawer's ✕ — never under it
        <span className="flex items-center gap-2">
          <span className={panel.enabled ? 'text-ok' : 'text-text-3'}>
            {panel.enabled ? 'enabled' : 'disabled'}
          </span>
          <Link to={`/panels/${panel.id}`} className={`${detailActionClass} ml-auto`}>Edit →</Link>
        </span>
      )}
    >
      <KeyValue
        items={[
          ['Projects', <ScopeChips scope={panel.project_scope} />],
          ['Placement', `${panel.placement} · render as ${panel.render_identity}`],
          ['Router', panel.expose_in_router ? 'exposed in the Connect router' : <span className="text-text-3">not exposed</span>],
          ['Blocks', <span className="font-mono">{panel.config?.blocks?.length ?? 0}</span>],
          ['Slug', <span className="font-mono text-[12px] text-text-2">/{panel.slug}</span>],
        ]}
      />
      <DetailSection title="Embed">
        <div className="space-y-2">
          <UrlRow label="SR4JC URL" value={panel.sr_url} />
          <UrlRow label="Direct URL" value={panel.url_template} />
        </div>
      </DetailSection>
      <DetailSection title="Preview">
        <SectionBoundary label="Preview">
          <PanelPreview panelId={panel.id} compact height={220} />
        </SectionBoundary>
      </DetailSection>
    </DetailPane>
  )
}

export default function PanelsList() {
  const navigate = useNavigate()
  const [panels, setPanels] = useState<Panel[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [placement, setPlacement] = useState<FacetValue>('any')
  const [project, setProject] = useState<FacetValue>('any')
  const [enabled, setEnabled] = useState<FacetValue>('any')
  const [sel, setSel] = useSelection()

  const refresh = useCallback(() => {
    setError(null)
    listPanels()
      .then((r) => setPanels(Array.isArray(r) ? r : []))
      .catch((e: Error) => { setError(e.message); setPanels([]) })
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // The row takes the server's answer, so the next toggle starts from current data.
  function handleUpdated(updated: Panel) {
    setPanels((prev) => (prev ?? []).map((p) => (p.id === updated.id ? updated : p)))
  }

  const all = panels ?? []
  const projects = useMemo(
    () => Array.from(new Set(all.flatMap((p) => p.project_scope ?? []))).sort(),
    [all],
  )
  const rows = all.filter((p) => {
    const needle = q.trim().toLowerCase()
    if (needle && !p.name.toLowerCase().includes(needle)
      && !(p.project_scope ?? []).some((k) => k.toLowerCase().includes(needle))) return false
    if (placement !== 'any' && p.placement !== placement) return false
    if (project !== 'any' && !(p.project_scope ?? []).includes(project)) return false
    if (enabled !== 'any' && p.enabled !== (enabled === 'enabled')) return false
    return true
  })
  const selected = all.find((p) => p.id === sel) ?? null

  const columns: Column<Panel>[] = [
    { key: 'name', header: 'Name', width: '22%' },
    {
      key: 'blocks', header: 'Blocks', render: (p) => <BlockTags types={p.block_types ?? []} />,
      title: (p) => Array.from(new Set(p.block_types ?? [])).join(', ') || undefined,
    },
    {
      key: 'projects', header: 'Projects', width: '14%', render: (p) => <ScopeChips scope={p.project_scope} />,
      title: (p) => (p.project_scope ?? []).join(', ') || 'any',
    },
    { key: 'placement', header: 'Placement', width: '96px' },
    { key: 'render_identity', header: 'Render as', width: '92px' },
    {
      key: 'enabled', header: 'Enabled', width: '84px', title: () => undefined,
      render: (p) => <EnabledSwitch panel={p} onUpdated={handleUpdated} />,
    },
  ]

  const enabledCount = all.filter((p) => p.enabled).length

  return (
    <Page
      title="Panels"
      hint="HTML panels rendered inside Jira issue views (SR4JC fragments, the Connect web panel, the Forge panel)."
      context={(
        <>
          {panels && (
            <span className="rounded-full bg-surface-active px-2.5 py-0.5 text-[11.5px] text-text-2">
              {plural(all.length, 'panel')} · {enabledCount} enabled
            </span>
          )}
          <span className="ml-auto">
            <SearchInput value={q} onChange={setQ} placeholder="Filter by name or project" label="Filter panels" width="w-[240px]" />
          </span>
        </>
      )}
      actions={(
        <Button size="sm" onClick={() => navigate('/panels/new')}>
          <Plus className="size-3.5" aria-hidden />
          New panel
        </Button>
      )}
    >
      <FilterBar>
        <Facet
          label="Placement"
          value={placement}
          onChange={setPlacement}
          options={[{ value: 'content', label: 'content' }, { value: 'sidebar', label: 'sidebar' }]}
        />
        <Facet label="Project" value={project} onChange={setProject} options={projects.map((k) => ({ value: k, label: k }))} />
        <Facet
          label="Enabled"
          value={enabled}
          onChange={setEnabled}
          options={[{ value: 'enabled', label: 'enabled' }, { value: 'disabled', label: 'disabled' }]}
        />
        {error && <span role="alert" className="ml-auto text-danger">Couldn’t load panels: {error}</span>}
      </FilterBar>

      {panels === null
        ? <LoadingRows label="Loading panels" />
        : all.length === 0 && !error
          ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <PanelsTopLeft aria-hidden className="size-6 text-text-3" />
              <div>
                <p className="text-[13px] text-foreground">No panels yet.</p>
                <p className="mt-1 text-[12px] text-text-2">
                  Create a panel to surface issue data or a JQL table inside Jira issue views.
                </p>
              </div>
              <Button size="sm" onClick={() => navigate('/panels/new')}>
                <Plus className="size-3.5" aria-hidden />
                New panel
              </Button>
            </div>
          )
          : (
            <SplitView
              onCloseDetail={() => setSel(null)}
              detail={selected && <PanelDetail panel={selected} />}
              list={(
                <DataTable
                  label="Panels"
                  rows={rows}
                  columns={columns}
                  rowId={(p) => p.id}
                  selectedId={sel}
                  onSelect={setSel}
                  empty={error ? undefined : "No panels match the filters."}
                />
              )}
            />
          )}
    </Page>
  )
}
